import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Brain, Heart, FileText, Download, Info } from 'lucide-react';
import { exportAPI } from '../../services/api';

type ReportType = 'dolores' | 'tensiones' | 'ambos';
type PeriodType = 'month' | 'year' | 'custom';

interface MedicalReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MedicalReportModal: React.FC<MedicalReportModalProps> = ({ isOpen, onClose }) => {
  const [reportType, setReportType] = useState<ReportType>('ambos');
  const [period, setPeriod] = useState<PeriodType>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Calcular fechas segun el periodo seleccionado
  const getDates = (): { startDate: string; endDate: string } | null => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];

    if (period === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }

    if (period === 'year') {
      const start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }

    if (period === 'custom') {
      if (!customStart || !customEnd) return null;
      return { startDate: customStart, endDate: customEnd };
    }

    return null;
  };

  const handleGenerate = async () => {
    setError('');
    const dates = getDates();

    if (!dates) {
      setError('Selecciona las fechas de inicio y fin');
      return;
    }

    if (dates.startDate > dates.endDate) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    setIsGenerating(true);
    try {
      const blob = await exportAPI.generateReport({
        type: reportType,
        startDate: dates.startDate,
        endDate: dates.endDate,
      });

      // Descargar el PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `informe-medico-${dates.startDate}-${dates.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error al generar el informe. Inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  const typeOptions: { value: ReportType; label: string; icon: React.ElementType }[] = [
    { value: 'dolores', label: 'Dolores', icon: Brain },
    { value: 'tensiones', label: 'Tensión', icon: Heart },
    { value: 'ambos', label: 'Ambos', icon: FileText },
  ];

  const periodOptions: { value: PeriodType; label: string }[] = [
    { value: 'month', label: 'Mes actual' },
    { value: 'year', label: 'Último año' },
    { value: 'custom', label: 'Personalizado' },
  ];

  const modalContent = (
    <div className="space-y-5">
      {/* Tipo de informe */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          Tipo de informe
        </label>
        <div className="flex gap-2">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setReportType(opt.value)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all text-sm ${
                reportType === opt.value
                  ? 'bg-[var(--color-accent)] text-white shadow-sm'
                  : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              <opt.icon size={16} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Periodo */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          Período
        </label>
        <div className="flex gap-2">
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`flex-1 py-3 rounded-xl font-medium transition-all text-sm ${
                period === opt.value
                  ? 'bg-[var(--color-accent)] text-white shadow-sm'
                  : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fechas personalizadas */}
      <AnimatePresence>
        {period === 'custom' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[var(--color-text-muted)] mb-1">Desde</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-muted)] mb-1">Hasta</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="input text-sm"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-500 text-center">{error}</p>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 p-3 rounded-xl bg-[var(--color-bg)]">
        <Info size={16} className="text-[var(--color-accent)] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[var(--color-text-muted)]">
          El informe se generará en formato PDF, listo para presentar a tu médico.
        </p>
      </div>

      {/* Boton generar */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={handleGenerate}
        disabled={isGenerating}
        className="btn btn-primary w-full py-4"
      >
        {isGenerating ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Generando...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Download size={20} />
            Generar informe PDF
          </div>
        )}
      </motion.button>
    </div>
  );

  return (
    <AnimatePresence>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      />

      {/* Mobile: Bottom sheet */}
      <motion.div
        initial={{ opacity: 0, y: '100%' }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[101] p-6 pb-safe rounded-t-3xl max-h-[85vh] overflow-y-auto"
        style={{ background: 'var(--color-surface)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
            Informe médico
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
          >
            <X size={24} className="text-[var(--color-text-muted)]" />
          </button>
        </div>
        {modalContent}
      </motion.div>

      {/* Desktop: Centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[101] items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md bg-[var(--color-surface)] rounded-3xl shadow-xl p-6 pointer-events-auto max-h-[85vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
              Informe médico
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
            >
              <X size={24} className="text-[var(--color-text-muted)]" />
            </button>
          </div>
          {modalContent}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
